import { useState } from 'react';
import { useGetAvailableChatRooms, useGetChatRoomUsers, useJoinChatRoom } from '../../Core/Hooks/admin/useChat';
import { useSignalRChat } from '../../Core/Hooks/useSignalRChat';
import { ChatRoom, ChatRoomType } from '../../Core/Types/admin/chat';
import { ObjectType } from '../../Core/Types/base';
import './ChatRoomsList.scss';
import ChatUsers from './ChatUsers';
import { useGlobalState } from '../../Core/Contexts/globalStateContext';
import { useTranslation } from 'react-i18next';
import BizIconButton from '../../Core/Components/Mini/BizIconButton';
import BizUserImage from '../../Core/Components/Mini/BizUserImage';
import { BizIcon } from '../../Core/Components/Mini/BizIcon';
import { BizButton } from '../../Core/Components/Mini/BizButton';

type ChatRoomsListProps = {
	onRoomSelect: (room: ChatRoom) => void;
	selectedRoom?: ChatRoom | null;
	objectType?: ObjectType; // Optional: filter rooms by object type
	objectId?: number; // Optional: filter rooms by object ID
	chatFuncs: ReturnType<typeof useSignalRChat>;
	onCreateDirectMessageDone: (newRoomId: number) => void;
	showRoomTypes?: ChatRoomType[]; // Optional: filter rooms by specific types
};

type ChatRoomListItemProps = {
	room: ChatRoom;
	selectedRoom?: ChatRoom | null;
	chatFuncs: ReturnType<typeof useSignalRChat>;
	currentUserId: number;
	isExpanded: boolean;
	onRoomClick: (room: ChatRoom) => void;
	onToggleExpanded: (roomId: number) => void;
	onCreateDirectMessageDone: (newRoomId: number) => void;
};

const ChatRoomListItem = ({
	room,
	selectedRoom,
	chatFuncs,
	currentUserId,
	isExpanded,
	onRoomClick,
	onToggleExpanded,
	onCreateDirectMessageDone,
}: ChatRoomListItemProps) => {
	const { t } = useTranslation();
	const { roomsState, roomUsersByRoomId } = chatFuncs;
	const canShowRoomMembers = room.VisibleUserList && room.Type !== ChatRoomType.DirectMessage;
	const hasSignalRRoomUsers = Object.prototype.hasOwnProperty.call(roomUsersByRoomId, room.Id);
	const {
		data: requestedRoomUsers,
		statusState: roomUsersStatus,
		refetch: refetchRoomUsers,
	} = useGetChatRoomUsers(room.Id, canShowRoomMembers && isExpanded && !hasSignalRRoomUsers);
	const roomUsers = hasSignalRRoomUsers ? roomUsersByRoomId[room.Id] : requestedRoomUsers;
	const hasLoadedRoomUsers = roomUsers !== undefined;
	const isLoadingRoomUsers =
		isExpanded && !hasLoadedRoomUsers && (roomUsersStatus === 'INITIAL' || roomUsersStatus === 'LOADING');
	const hasRoomUsersError = isExpanded && !hasLoadedRoomUsers && roomUsersStatus === 'ERROR';
	const directMessageMember =
		room.Type === ChatRoomType.DirectMessage
			? (room.Members?.find((member) => member.UserId !== currentUserId) ?? null)
			: null;
	const membersRegionId = `chat-room-${room.Id}-members`;
	const toggleTitle = isExpanded
		? t('CHAT_ROOMS_LIST.HIDE_MEMBERS', { defaultValue: 'Hide members' })
		: t('CHAT_ROOMS_LIST.SHOW_MEMBERS', { defaultValue: 'Show members' });
	const roomIndicators = (
		<>
			{roomsState[room.Id]?.hasMoreMessages && (
				<span
					className='new-messages-indicator'
					aria-label={t('CHAT_ROOMS_LIST.NEW_MESSAGES', { defaultValue: 'New Messages' })}
				/>
			)}
			<span className='room-members-toggle-slot'>
				{canShowRoomMembers && (
					<BizIconButton
						icon={isExpanded ? 'CHEVRON-UP' : 'CHEVRON-DOWN'}
						noBackground
						legacy={false}
						size='SMALL'
						title={toggleTitle}
					/>
				)}
			</span>
		</>
	);

	return (
		<div
			className={`room-item ${selectedRoom?.Id === room.Id ? 'selected' : ''} ${roomsState[room.Id]?.hasMoreMessages ? 'has-new-messages' : ''}`}
		>
			<div className='room-info' onClick={() => onRoomClick(room)}>
				<div className='room-main'>
					<div className={`room-header ${directMessageMember ? 'has-room-avatar' : ''}`}>
						{directMessageMember && (
							<BizUserImage
								imageUrl={directMessageMember.UserPhoto}
								fullname={directMessageMember.UserDisplayName}
								size={30}
							/>
						)}
						<h5 className='room-name'>{room.ExternalName}</h5>
					</div>
					{canShowRoomMembers && hasLoadedRoomUsers && (
						<div className='room-meta'>
							<span className='member-count'>
								{t('CHAT_ROOMS_LIST.MEMBER_COUNT', {
									count: roomUsers.length,
									defaultValue: '{{count}} members',
								})}
							</span>
						</div>
					)}
				</div>
				{canShowRoomMembers ? (
					<button
						type='button'
						className='room-indicators room-indicators--expandable'
						onClick={(event) => {
							event.stopPropagation();
							onToggleExpanded(room.Id);
						}}
						aria-expanded={isExpanded}
						aria-controls={membersRegionId}
						aria-label={toggleTitle}
						title={toggleTitle}
					>
						{roomIndicators}
					</button>
				) : (
					<div className='room-indicators'>{roomIndicators}</div>
				)}
			</div>

			{canShowRoomMembers && isExpanded && (
				<div className='room-expandable' id={membersRegionId}>
					{isLoadingRoomUsers && (
						<div className='room-members-status' aria-live='polite'>
							<BizIcon icon='LOADING' />
							<span>{t('CHAT_ROOMS_LIST.LOADING_MEMBERS', { defaultValue: 'Loading members...' })}</span>
						</div>
					)}
					{hasRoomUsersError && (
						<div className='room-members-error' role='alert'>
							<span>{t('CHAT_ROOMS_LIST.LOAD_MEMBERS_ERROR', { defaultValue: 'Could not load members.' })}</span>
							<BizButton
								text={t('CHAT_ROOMS_LIST.RETRY', { defaultValue: 'Retry' })}
								onClick={() => void refetchRoomUsers()}
								legacy={false}
								size='SMALL'
							/>
						</div>
					)}
					{hasLoadedRoomUsers && (
						<div className='room-members'>
							<ChatUsers
								users={roomUsers}
								currentUserId={currentUserId}
								onCreateDirectMessageDone={onCreateDirectMessageDone}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

const ChatRoomsList = ({
	onRoomSelect,
	selectedRoom,
	objectType,
	objectId,
	chatFuncs,
	onCreateDirectMessageDone,
	showRoomTypes,
}: ChatRoomsListProps) => {
	const { t } = useTranslation();
	const {
		state: { user },
	} = useGlobalState();
	const { data: availableRooms, statusState: loadingRooms } = useGetAvailableChatRooms(objectType, objectId);
	const { joinChatRoom } = useJoinChatRoom();
	const [expandedRoomIds, setExpandedRoomIds] = useState<Record<number, boolean>>({});

	const toggleRoomExpanded = (roomId: number) => {
		setExpandedRoomIds((prev) => ({
			...prev,
			[roomId]: !prev[roomId],
		}));
	};

	const handleRoomClick = (room: ChatRoom) => {
		if (selectedRoom?.Id === room.Id) {
			toggleRoomExpanded(room.Id);
			return; // Already selected
		}
		// Join the room first, then select it
		joinChatRoom(room.Id);
		onRoomSelect(room);
	};

	if (
		showRoomTypes &&
		showRoomTypes.length === 1 &&
		showRoomTypes[0] === ChatRoomType.DirectMessage &&
		availableRooms?.length === 0
	)
		return null;

	return (
		<div className='biz-chat-rooms-list'>
			<div className='rooms-header'>
				<h4>
					{showRoomTypes && showRoomTypes.length === 1 && showRoomTypes[0] === ChatRoomType.DirectMessage
						? t('CHAT_ROOMS_LIST.CHATS', { defaultValue: 'Chats' })
						: t('CHAT_ROOMS_LIST.CHAT_ROOMS', { defaultValue: 'Chat rooms' })}
				</h4>
			</div>

			<div className='rooms-content'>
				{loadingRooms === 'LOADING' && (
					<div className='loading'>
						{t('CHAT_ROOMS_LIST.LOADING_CHAT_ROOMS', { defaultValue: 'Loading chat rooms...' })}
					</div>
				)}

				{availableRooms && availableRooms.length === 0 && (
					<div className='no-rooms'>
						{t('CHAT_ROOMS_LIST.NO_CHAT_ROOMS_AVAILABLE', { defaultValue: 'No chat rooms available.' })}
					</div>
				)}

				{availableRooms
					?.filter(
						(room) =>
							!showRoomTypes ||
							(showRoomTypes?.includes(ChatRoomType.Group) && room.Type === ChatRoomType.Group) ||
							(showRoomTypes?.includes(ChatRoomType.DirectMessage) && room.Type === ChatRoomType.DirectMessage)
					)
					?.map((room) => (
						<ChatRoomListItem
							key={room.Id}
							room={room}
							selectedRoom={selectedRoom}
							chatFuncs={chatFuncs}
							currentUserId={user?.Id ?? 0}
							isExpanded={!!expandedRoomIds[room.Id]}
							onRoomClick={handleRoomClick}
							onToggleExpanded={toggleRoomExpanded}
							onCreateDirectMessageDone={onCreateDirectMessageDone}
						/>
					))}
			</div>
		</div>
	);
};

export default ChatRoomsList;
